import React from "react";
import { registerOfficeDropTarget } from "../../../../../../../utilities/outlookDropTargets";

// A no-op stand-in for the React drag/click event that tile save handlers expect. The
// pane-global Office.js drop has no DOM event, and those handlers only ever call
// preventDefault/stopPropagation/persist on it (never target/coordinates), so no-ops
// are safe. Used by tiles whose save-navigation callback takes an event argument.
export const SYNTHETIC_OFFICE_DROP_EVENT = {
    preventDefault: () => { },
    stopPropagation: () => { },
    persist: () => { },
} as any;

export interface IOfficeDropTargetOptions {
    // Save-navigation run AFTER the pane-global handler has staged the dropped bytes
    // (identical to the tile's DOM onDrop post-staging step). Omit for hover-only tiles
    // (sites/Tess: expand affordance without drop support).
    onDrop?: () => void;
    // Mirrors the tile's isValidDropZone; gates onDrop. Default: true when onDrop exists.
    // A nullish RETURN value counts as "not droppable" — it never falls back to the default.
    canDrop?: () => boolean;
    // Hover parity: wire to the tile's setIsDraggedOver so the Office.js drag drives the
    // SAME affordance (blue/grey border) and hover timers (auto-expand, breadcrumb
    // navigate) as the DOM dragEnter/dragLeave path.
    onDragHoverChange?: (isOver: boolean) => void;
    // Hover interactivity while NOT droppable (grey affordance). Defaults to droppability;
    // pass () => true for tiles whose DOM path always reacts to a hover.
    canHover?: () => boolean;
}

// Registers a tile's root element + its drag-and-drop behavior with the pane-global
// Office.js drop registry for the tile's lifetime. Latest callbacks are read through a
// ref so the registration itself never churns. Which callbacks are PRESENT must be
// stable across renders (their identity may change freely) — presence is captured once
// at registration time.
export function useOfficeDropTarget(
    ref: React.RefObject<HTMLElement>,
    options: IOfficeDropTargetOptions
): void {
    const optionsRef = React.useRef(options);
    optionsRef.current = options;

    // The element currently registered and its unregister fn, tracked across renders.
    // We must reconcile on EVERY commit (no dependency array) rather than key an effect
    // on the stable `ref` object: `ref.current` can be null when the effect first runs
    // (a conditionally-rendered root — e.g. MainLocationItem's collapsed side-menu) or
    // can swap to a different element later. A `[ref]`-keyed effect would capture
    // ref.current exactly once and never see the element appear or change, silently
    // dropping the tile from the registry.
    const registeredEl = React.useRef<HTMLElement | null>(null);
    const unregister = React.useRef<(() => void) | null>(null);

    React.useEffect(() => {
        const element = ref.current;
        if (element === registeredEl.current) {
            return;
        }
        if (unregister.current) {
            unregister.current();
            unregister.current = null;
        }
        registeredEl.current = element;
        if (element) {
            const current = optionsRef.current;
            unregister.current = registerOfficeDropTarget({
                element,
                onDrop: current.onDrop ? () => optionsRef.current.onDrop?.() : undefined,
                // `!!` coerces a present callback's nullish return to false: registrants'
                // gates are `a && b` chains that can evaluate to null (strict:false), and
                // `?? true` here would turn "no droppable location" into "droppable".
                // `?.()` fails CLOSED if the callback ever vanishes through the ref — a
                // state the presence-stability contract above forbids, so it must read
                // as a bug (not droppable), never as "droppable by default".
                canDrop: current.canDrop ? () => !!optionsRef.current.canDrop?.() : undefined,
                onDragHoverChange: current.onDragHoverChange
                    ? (isOver: boolean) => optionsRef.current.onDragHoverChange?.(isOver)
                    : undefined,
                canHover: current.canHover ? () => optionsRef.current.canHover?.() ?? false : undefined,
            });
        }
    });

    // Unmount cleanup: the reconcile effect above intentionally returns no teardown
    // (it manages lifecycle via the persisted refs), so unregister here on unmount.
    React.useEffect(() => {
        return () => {
            if (unregister.current) {
                unregister.current();
                unregister.current = null;
            }
            registeredEl.current = null;
        };
    }, []);
}
